// OCT-7340: TreatyApp: User Management — add an Action History view of admin activity - Frontend : ashish.atwal@resolutionlife.com
// Filter row for the Action History drawer: an Action dropdown, a User dropdown
// (options derived from the loaded records), From/To date pickers, and a Clear
// button. All filtering is applied client-side by the panel over the loaded
// entries, so these controls only report the selected filter values upward.
import { ChangeEvent, FC } from 'react';
import { Button, Flex, Select } from '@chakra-ui/react';

import { DatePicker, InputWithLabel } from '../date-picker/date-picker';

export type ActionHistoryFilterState = {
  actionType: string;
  userId: string;
  from: Date | null;
  to: Date | null;
};

export const emptyActionHistoryFilterState: ActionHistoryFilterState = {
  actionType: '',
  userId: '',
  from: null,
  to: null,
};

export type ActionHistoryUserOption = { id: string; name: string };

type Props = {
  users: ActionHistoryUserOption[];
  actionTypes: string[];
  filter: ActionHistoryFilterState;
  onChange: (filter: ActionHistoryFilterState) => void;
};

export const isFilterActive = (filter: ActionHistoryFilterState): boolean =>
  !!filter.actionType || !!filter.userId || !!filter.from || !!filter.to;

export const ActionHistoryFilters: FC<Props> = ({
  users,
  actionTypes,
  filter,
  onChange,
}) => {
  const handleActionChange = (e: ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...filter, actionType: e.target.value });
  };

  const handleUserChange = (e: ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...filter, userId: e.target.value });
  };

  const handleClear = () => {
    onChange(emptyActionHistoryFilterState);
  };

  return (
    <Flex gridGap={3} align="flex-end" wrap="wrap">
      <Select
        placeholder="All actions"
        value={filter.actionType}
        onChange={handleActionChange}
        data-testid="action-history-action-filter"
        maxW="52"
      >
        {actionTypes.map((label) => (
          <option key={label} value={label}>
            {label}
          </option>
        ))}
      </Select>

      <Select
        placeholder="All users"
        value={filter.userId}
        onChange={handleUserChange}
        data-testid="action-history-user-filter"
        maxW="52"
      >
        {users.map((user) => (
          <option key={user.id} value={user.id}>
            {user.name}
          </option>
        ))}
      </Select>

      <DatePicker
        selected={filter.from}
        onChange={(date: Date | null) => onChange({ ...filter, from: date })}
        customInput={<InputWithLabel label="From" />}
      />

      <DatePicker
        selected={filter.to}
        onChange={(date: Date | null) => onChange({ ...filter, to: date })}
        customInput={<InputWithLabel label="To" />}
      />

      <Button
        variant="outline"
        onClick={handleClear}
        isDisabled={!isFilterActive(filter)}
        data-testid="action-history-clear-filters"
      >
        Clear
      </Button>
    </Flex>
  );
};
